import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Collapse,
} from "@mui/material";

import VoicemailIcon from "@mui/icons-material/Voicemail";
import Group from "@mui/icons-material/Group";
import Phone from "@mui/icons-material/Phone";
import Numbers from "@mui/icons-material/Numbers";
import Description from "@mui/icons-material/Description";
import Settings from "@mui/icons-material/Settings";
import AccountTree from "@mui/icons-material/AccountTree";
import AssessmentIcon from "@mui/icons-material/Assessment";

import { NavLink, useNavigate } from "react-router-dom";
import { FaUsers, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";

import { setModal } from "../../features/modal/ModalSlice";
import { clearUser } from "../../features/user/UserSlice";
import { removeSipConfig } from "../../features/SIP/SIPConfigSlice";
import CustomButton from "../Button/CustomButton";

const listItemStyles = {
  borderRadius: "8px",
  "&.active": { backgroundColor: "#F2F2F2" },
};

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const userInfo = useSelector((state) => state.userInfo.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAddMember = () => {
    dispatch(setModal({ type: "ADD_MEMBER" }));
  };

  const handleLogout = () => {
    dispatch(clearUser());
    dispatch(removeSipConfig());
    navigate("/");
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        "& .MuiDrawer-paper": {
          width: 240,
          boxSizing: "border-box",
          backgroundColor: "#fff",
          padding: 1,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          onClick={toggleAccordion}
          style={{ cursor: "pointer" }}
        >
          <Box
            sx={{
              backgroundColor: "#E5E1FA",
              width: 50,
              height: 50,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginRight: 1,
              fontSize: 24,
              color: "#4C4A8D",
              fontWeight: "bold",
            }}
          >
            {userInfo?.name?.substring(0, 1)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userInfo?.name.length > 10
                ? userInfo?.name.substring(0, 10) + "..."
                : userInfo?.name}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, color: "#666" }}>
              {/* 1 User */}
              {userInfo?.role === "2" ? "admin" : "user"}
            </Typography>
          </Box>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </Box>
        <Collapse in={isExpanded}>
          <List sx={{ marginTop: 1 }}>
            <ListItemButton component={NavLink} to={"/settings"} sx={listItemStyles}>
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItemButton>
            <CustomButton
              icon={<LogoutIcon />}
              iconPosition="left"
              width={"100%"}
              onClick={handleLogout}
            >
              Log out
            </CustomButton>
          </List>
        </Collapse>

        {userInfo?.role === "2" && (
          <CustomButton
            text={"Invite Member"}
            width="100%"
            variant="outlined"
            className="my-3"
            onClick={handleAddMember}
          />
        )}
      </Box>

      <Divider />

      {/* Menu Items */}

      <List>
        {userInfo && userInfo?.role === "2" && (
          <>
            <ListItemButton component={NavLink} to={"/users"} sx={listItemStyles}>
              <ListItemIcon>
                <Group />
              </ListItemIcon>
              <ListItemText primary="Teams Members" />
            </ListItemButton>
            <ListItemButton component={NavLink} to={"/invites"} sx={listItemStyles}>
              <ListItemIcon>
                <FaUsers />
              </ListItemIcon>
              <ListItemText primary="Invites" />
            </ListItemButton>
          </>
        )}

        <ListItemButton component={NavLink} to={"/call"} sx={listItemStyles}>
          <ListItemIcon>
            <Phone />
          </ListItemIcon>
          <ListItemText primary="Calls" />
        </ListItemButton>

        <ListItemButton component={NavLink} to={"/reports"} sx={listItemStyles}>
          <ListItemIcon>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText primary="Reports" />
        </ListItemButton>

        <ListItemButton component={NavLink} to={"/voice-mail"} sx={listItemStyles}>
          <ListItemIcon>
            <VoicemailIcon />
          </ListItemIcon>
          <ListItemText primary="Voice Mail" />
        </ListItemButton>

        {userInfo && userInfo?.role === "3" && (
          <ListItemButton component={NavLink} to={"/extensions"} sx={listItemStyles}>
            <ListItemIcon>
              <Numbers />
            </ListItemIcon>
            <ListItemText primary="Your Numbers" />
          </ListItemButton>
        )}

        {userInfo && userInfo?.role === "2" && (
          <>
            <ListItemButton component={NavLink} to={"/extensions-allocation"} sx={listItemStyles}>
              <ListItemIcon>
                <Numbers />
              </ListItemIcon>
              <ListItemText primary="Your Numbers" />
            </ListItemButton>
            <ListItemButton component={NavLink} to={"/requests"} sx={listItemStyles}>
              <ListItemIcon>
                <Description />
              </ListItemIcon>
              <ListItemText primary="Your Requests" />
            </ListItemButton>

            <ListItemButton component={NavLink} to={"/ai-agent"} sx={listItemStyles}>
              <ListItemIcon>
                <AccountTree />
              </ListItemIcon>
              <ListItemText primary="AI Agents" />
            </ListItemButton>

            <ListItemButton component={NavLink} to={"/error-logs"} sx={listItemStyles}>
              <ListItemIcon>
                <ReportGmailerrorredIcon />
              </ListItemIcon>
              <ListItemText primary="Error Logs" />
            </ListItemButton>

            <ListItemButton component={NavLink} to={"/settings"} sx={listItemStyles}>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </>
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;
